import {Component, OnDestroy, OnInit} from '@angular/core';
import {SecurityService} from '../../../../security/security.service';
import {
  UpdateUserPasswordModel,
  UserModel,
  UserModelUtil,
  UserModelView
} from '../../../../shared/models/User';
import {UserManagementService} from '../../../../data/users/user-management.service';
import {UntypedFormBuilder, UntypedFormGroup, Validators} from '@angular/forms';
import {MatSnackBar} from '@angular/material/snack-bar';
import {ConfigurationService} from '../../../../configuration/configuration.service';
import {ActivatedRoute, Router} from '@angular/router';
import {firstValueFrom, Subscription} from 'rxjs';
import { DialogEditUserComponent } from '../manage-users/dialogs/dialog-edit-user.component';
import { MatDialog } from '@angular/material/dialog';
import { UserEditMode } from '../manage-users/edit-user/edit-user.component';
import { DialogChangePasswordComponent } from '../manage-users/dialogs/dialog-change-password.component';
import {TenantApiKeysService} from '../../../../data/tenant-api-keys/tenant-api-keys.service';
import {TenantApiKey} from '../../../../shared/models/tenant-api-key';
import { CardComponent } from '../../../../shared/components/card/card.component';
import { MatButton } from '@angular/material/button';
import { CardContentDirective } from '../../../../shared/components/card/card-content.directive';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { CdkCopyToClipboard } from '@angular/cdk/clipboard';
import { MatTooltip } from '@angular/material/tooltip';
import { MatIcon } from '@angular/material/icon';
import { DatePipe } from '@angular/common';

@Component({
  selector: 'app-my-account',
  templateUrl: './my-account.component.html',
  styleUrls: ['./my-account.component.scss', '../../../settings/settings-fields.scss', '../../settings-common.scss'],
  imports: [CardComponent, MatButton, CardContentDirective, MatProgressSpinner, CdkCopyToClipboard, MatTooltip, MatIcon, DatePipe],
})
export class MyAccountComponent implements OnInit, OnDestroy {
  isLoading = true;
  isEditing = false;
  isResetting = false;
  keysLoading = false;
  user: UserModel;
  customerId: string;

  passwordUpdateForm: UntypedFormGroup;
  apiKeys: TenantApiKey[];
  private tenantId: number;
  private $configuration: Subscription;

  constructor(
    private readonly securityService: SecurityService,
    private readonly userManagementService: UserManagementService,
    private readonly configurationService: ConfigurationService,
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private fb: UntypedFormBuilder,
    private snackBar: MatSnackBar,
    private dialog: MatDialog,
    private readonly tenantApiKeysService: TenantApiKeysService
  ) {
    this.$configuration = this.configurationService.sharedConfigurationModel.subscribe(tenantConfiguration => {
      if (tenantConfiguration) {
        this.tenantId = tenantConfiguration.id;
        this.loadKeys();
      }
    });
    this.initForm();
  }

  ngOnDestroy(): void {
    if (this.$configuration) {
      this.$configuration.unsubscribe();
    }
  }

  initForm() {
    this.passwordUpdateForm = this.fb.group(
      {
        newPassword: '',
        verifyPassword: '',
      },
      {
        validators: [this.passwordValidator],
      }
    );
    this.passwordUpdateForm.controls['newPassword'].setValidators([
      Validators.required,
      Validators.minLength(8),
      Validators.pattern('(?=.*\\d)(?=.*[a-z])(?=.*[A-Z])(?=.*\\W).{4,}'),
    ]);
  }

  passwordValidator(form: UntypedFormGroup) {
    const condition = form.get('newPassword').value !== form.get('verifyPassword').value;
    return condition ? { passwordsDoNotMatch: true } : null;
  }

  ngOnInit() {
    this.isEditing = this.activatedRoute.snapshot.queryParams.edit === 'true';
    this.isResetting = this.activatedRoute.snapshot.queryParams.reset === 'true';

    this.activatedRoute.queryParams.subscribe(params => {
      this.isEditing = params.edit === 'true';
      this.isResetting = params.reset === 'true';

      if (!this.isEditing && !this.isResetting) {
        this.passwordUpdateForm.reset();
      }
    });

    this.configurationService.getCustomerId().then(response => {
      this.customerId = response.data;
    });

    this.isLoading = true;
    this.securityService
      .getUserEmail()
      .then(email => {
        firstValueFrom(this.userManagementService.getUser(email))
          .then(response => {
            this.user = response.data;
          })
          .catch(error => {
            console.error('cannot load user data', error);
          });
      })
      .finally(() => {
        this.isLoading = false;
      });
  }

  /**
   * Fetches and loads API keys for a tenant from the service.
   * @return void, This method does not return a value.
   */
  private loadKeys() {
    this.keysLoading = true;
    if (!!this.tenantId) {
      firstValueFrom(this.tenantApiKeysService.getApiKeys())
        .then(response => {
          this.apiKeys = response.data;
        })
        .catch(error => {
          console.error('cannot load keys', error);
        })
        .finally(() => {
          this.keysLoading = false;
        });
    }
  }

  save() {
    this.isLoading = true;
    const updateModel = new UserModel();
    updateModel.email = this.user.email;
    updateModel.phoneNumber = this.user.phoneNumber;
    updateModel.givenName = this.user.givenName;
    updateModel.familyName = this.user.familyName;
    updateModel.roles = this.user.roles;
    firstValueFrom(this.userManagementService.updateUser(updateModel))
      .then(response => {
        this.user = response.data;
        this.isLoading = false;
        this.snackBar.open('User updated.', null, { duration: 2000 });
        this.router.navigate([], {
          queryParams: {
            edit: undefined,
          },
        });
      })
      .catch(message => {
        console.error(message);
      });
  }

  listRoles() {
    return this.user.roles.map(role => UserModelUtil.getRoleLabel(role)).join(', ');
  }

  updateUser() {
    // reference only
    const user = this.user;
    // const user = Object.assign({}, this.findUser(userId)); // shallow copy
    // const user = {...this.findUser(userId)}; // shallow copy

    // make deep copy
    const userCopy = JSON.parse(JSON.stringify(this.user));
    const dialogRef = this.dialog.open(DialogEditUserComponent, {
      width: '450px',
      data: {
        mode: UserEditMode.SELF_UPDATE,
        user: {
          email: userCopy.email,
          familyName: userCopy.familyName,
          givenName: userCopy.givenName,
          roles: userCopy.roles,
          phoneNumber: userCopy.phoneNumber,
        } as UserModelView,
      },
    });

    firstValueFrom(dialogRef.afterClosed()).then(result => {
      if (result) {
        Object.assign(user, UserModelView.preload(result, false));
        this.snackBar.open('User has been updated.');
      }
    });
  }

  changePassword() {
    const user = this.user;
    const dialogRef = this.dialog.open(DialogChangePasswordComponent, {
      width: '450px',
      data: {
        email: user.email,
        familyName: user.familyName,
        givenName: user.givenName,
      },
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result !== undefined && result) {
        this.userManagementService
          .updatePassword(new UpdateUserPasswordModel(user.email, result))
          .then(_ => {
            this.snackBar.open('Password has been updated.');
          })
          .catch(error => {
            this.snackBar.open(error);
          });
      }
    });
  }
}
